import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { api } from "./lib/api";
import { Toaster } from "./components/ui/sonner";
import { CartProvider }         from "./context/CartContext";
import { CustomerAuthProvider } from "./context/CustomerAuthContext";
import { AdminAuthProvider }    from "./context/AdminAuthContext";
import { SettingsProvider }     from "./context/SettingsContext";

import Home          from "./pages/store/Home";
import Products      from "./pages/store/Products";
import ProductDetail from "./pages/store/ProductDetail";
import Cart          from "./pages/store/Cart";
import Checkout      from "./pages/store/Checkout";
import OrderSuccess  from "./pages/store/OrderSuccess";
import Account       from "./pages/store/Account";
import AuthCallback  from "./pages/store/AuthCallback";
import Quote         from "./pages/store/Quote";
import Contact       from "./pages/store/Contact";
import B2B           from "./pages/store/B2B";
import Returns       from "./pages/store/Returns";
import About         from "./pages/store/About";
import ShippingPolicy from "./pages/store/ShippingPolicy";
import ReturnsPolicy  from "./pages/store/ReturnsPolicy";
import WarrantyPolicy from "./pages/store/WarrantyPolicy";
import PrivacyPolicy  from "./pages/store/PrivacyPolicy";
import RefundPolicy   from "./pages/store/RefundPolicy";

import AdminLogin     from "./pages/admin/AdminLogin";
import AdminLayout    from "./components/layout/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts  from "./pages/admin/AdminProducts";
import AdminOrders    from "./pages/admin/AdminOrders";
import AdminSync      from "./pages/admin/AdminSync";
import AdminSettings  from "./pages/admin/AdminSettings";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminMessages  from "./pages/admin/AdminMessages";
import AdminQuotes    from "./pages/admin/AdminQuotes";
import AdminPriceQuotes from "./pages/admin/AdminPriceQuotes";
import AdminPaymentLinks from "./pages/admin/AdminPaymentLinks";
import AdminInvoices  from "./pages/admin/AdminInvoices";
import AdminShipping  from "./pages/admin/AdminShipping";
import AdminCredits   from "./pages/admin/AdminCredits";
import AdminSyncQueue from "./pages/admin/AdminSyncQueue";
import AdminOffers    from "./pages/admin/AdminOffers";
import AdminB2B       from "./pages/admin/AdminB2B";
import AdminReturns   from "./pages/admin/AdminReturns";
import AdminConflicts from "./pages/admin/AdminConflicts";
import AdminAuditLog  from "./pages/admin/AdminAuditLog";
import AdminEmailLog  from "./pages/admin/AdminEmailLog";

import ChatWidget from "./components/ChatWidget";

function AppRouter() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isAuthCallback = location.hash && location.hash.includes("session_id=");

  // Lightweight, fire-and-forget page-view logging — feeds the admin
  // dashboard's own visitor analytics. Never blocks rendering, never
  // surfaces errors to the customer if it fails. Placed before the early
  // return below so hooks always run in the same order (Rules of Hooks).
  useEffect(() => {
    if (isAdmin || isAuthCallback) return;
    api.post("/track/pageview", {
      path: location.pathname,
      referrer: document.referrer || "",
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAdmin, isAuthCallback]);

  if (isAuthCallback) {
    return <AuthCallback />;
  }

  return (
    <>
      <Routes>
        <Route path="/"                element={<Home />} />
        <Route path="/shop"            element={<Products />} />
        <Route path="/product/:id"     element={<ProductDetail />} />
        <Route path="/cart"            element={<Cart />} />
        <Route path="/checkout"        element={<Checkout />} />
        <Route path="/order/success"   element={<OrderSuccess />} />
        <Route path="/order/confirmed" element={<OrderSuccess />} />
        <Route path="/account"         element={<Account />} />
        <Route path="/account/orders"  element={<Account />} />
        <Route path="/auth/callback"   element={<AuthCallback />} />
        <Route path="/quote"           element={<Quote />} />
        <Route path="/contact"         element={<Contact />} />
        <Route path="/b2b"             element={<B2B />} />
        <Route path="/returns"         element={<Returns />} />
        <Route path="/about"           element={<About />} />
        <Route path="/policies/shipping" element={<ShippingPolicy />} />
        <Route path="/policies/returns"  element={<ReturnsPolicy />} />
        <Route path="/policies/warranty" element={<WarrantyPolicy />} />
        <Route path="/policies/privacy"  element={<PrivacyPolicy />} />
        <Route path="/policies/refund"   element={<RefundPolicy />} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index             element={<AdminDashboard />} />
          <Route path="products"   element={<AdminProducts />} />
          <Route path="orders"     element={<AdminOrders />} />
          <Route path="customers"  element={<AdminCustomers />} />
          <Route path="payment-links" element={<AdminPaymentLinks />} />
          <Route path="invoices"   element={<AdminInvoices />} />
          <Route path="shipping"   element={<AdminShipping />} />
          <Route path="sync"       element={<AdminSync />} />
          <Route path="sync-queue" element={<AdminSyncQueue />} />
          <Route path="conflicts"  element={<AdminConflicts />} />
          <Route path="audit-log"  element={<AdminAuditLog />} />
          <Route path="email-log"   element={<AdminEmailLog />} />
          <Route path="settings"   element={<AdminSettings />} />
          <Route path="messages"   element={<AdminMessages />} />
          <Route path="quotes"     element={<AdminQuotes />} />
          <Route path="price-quotes" element={<AdminPriceQuotes />} />
          <Route path="credits"    element={<AdminCredits />} />
          <Route path="offers"     element={<AdminOffers />} />
          <Route path="b2b"        element={<AdminB2B />} />
          <Route path="returns"    element={<AdminReturns />} />
        </Route>

        <Route path="*" element={<Home />} />
      </Routes>
      {!isAdmin && <ChatWidget />}
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <SettingsProvider>
        <CustomerAuthProvider>
          <AdminAuthProvider>
            <CartProvider>
              <BrowserRouter>
                <AppRouter />
                <Toaster position="top-right" richColors />
              </BrowserRouter>
            </CartProvider>
          </AdminAuthProvider>
        </CustomerAuthProvider>
      </SettingsProvider>
    </div>
  );
}